import { Fragment, useMemo, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { EyeIcon } from 'lucide-react';
import CircuitDayPricing from './CircuitDayPricing';
import HorizontalLine from '../ui/HorizontalLine';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { addDays } from 'date-fns';
import { getCircuitTotalPrice } from '@/utils/circuits/prices/getCircuitTotalPrice';
import { computeCircuitDayMinimumPrice } from '@/utils/circuits/prices/computeCircuitPricePerDay';
import CircuitDetailsPricesCalendarCard from '../distributorCircuits/CircuitDetailsPricesCalendarCard';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import { useConvertPrice } from '@/hooks/useCurrencyExchange';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from '@/i18n';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';

interface CircuitSummaryProps {
  control: Control<CircuitFormDataWithTranslations>;
  selectedArrivalDate: Date;
  setSelectedArrivalDate: React.Dispatch<React.SetStateAction<Date>>;
  onCircuitDayClick?: (day: number) => void;
  language?: SupportedLocale;
}

const CircuitSummary = ({
  control,
  selectedArrivalDate,
  setSelectedArrivalDate,
  onCircuitDayClick,
  language,
}: CircuitSummaryProps) => {
  const { t } = useTranslation();
  const days = useWatch({ control, name: 'circuitDays' });
  const nbDays = useWatch({ control, name: 'nbDays' });
  const currencies = useWatch({ control, name: 'countries' });

  const currency = useMemo(() => {
    if (currencies.length === 0) {
      return 'EUR';
    }
    const mainCurrency = currencies[0].currency.value;
    return mainCurrency;
  }, [currencies]);

  const nbDaysArray = useMemo(
    () => Array.from(Array(Number(nbDays)).keys()),
    [nbDays],
  );

  const nbAdults = useWatch({ control, name: 'nbParticipants' });
  const nbAdolescents = useWatch({ control, name: 'nbAdolescents' });
  const nbChildren = useWatch({ control, name: 'nbChildren' });
  const circuitMarginRate = useWatch({ control, name: 'marginRate' });

  const totalParticipants = useMemo(() => {
    const raw =
      Number(nbAdults) + Number(nbChildren ?? 0) + Number(nbAdolescents ?? 0);
    return Number.isFinite(raw) && raw >= 1 ? raw : 1;
  }, [nbAdults, nbChildren, nbAdolescents]);

  const {
    daysTrackingMultiDayBenefits,
    circuitDaysObject,
    circuitDaysPriceByStartDate,
    findMinimumPriceForDate,
    lastPricedStartDate,
    ensurePricedUntil,
  } = useCircuitPrices({
    days: days,
    circuitMarginRate,
    nbParticipants: totalParticipants,
    nbChildren: Number(nbChildren ?? 0),
    nbAdolescents: Number(nbAdolescents ?? 0),
    targetCurrency: currency,
  });

  const minimumPriceWithDay = useMemo(
    () => getCircuitTotalPrice(circuitDaysPriceByStartDate),
    [circuitDaysPriceByStartDate],
  );
  const [defaultDate] = useState(() => selectedArrivalDate);

  const { convertPrice } = useConvertPrice();

  const dayPrices = useMemo(
    () =>
      minimumPriceWithDay.price === Infinity
        ? daysTrackingMultiDayBenefits.map((circuitDay, indexDay) =>
            computeCircuitDayMinimumPrice({
              circuitDay,
              date: addDays(selectedArrivalDate, indexDay),
              nbParticipants: totalParticipants,
              circuitMarginRate: Number(circuitMarginRate ?? 0),
              nbChildren: Number(nbChildren ?? 0),
              nbAdolescents: Number(nbAdolescents ?? 0),
              shouldIgnoreNoPrice: true,
              indexDay,
              targetCurrency: currency,
              convertPrice,
            }),
          )
        : circuitDaysPriceByStartDate(selectedArrivalDate),
    [
      circuitDaysPriceByStartDate,
      circuitMarginRate,
      convertPrice,
      currency,
      daysTrackingMultiDayBenefits,
      minimumPriceWithDay.price,
      nbAdolescents,
      nbChildren,
      selectedArrivalDate,
      totalParticipants,
    ],
  );

  const totalPrice = useMemo(
    () => dayPrices.reduce((a, b) => a + b, 0),
    [dayPrices],
  );

  return (
    <div className="flex flex-col items-center gap-4 p-6">
      <div className="flex items-center justify-center w-full xl:w-3/5">
        <CircuitDetailsPricesCalendarCard
          calculateMinimumPriceForDate={findMinimumPriceForDate}
          nbParticipants={totalParticipants}
          onDayClick={setSelectedArrivalDate}
          foldable={false}
          defaultDate={defaultDate}
          currency={currency}
          lastPricedStartDate={lastPricedStartDate}
          onMonthChange={ensurePricedUntil}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-6 w-full">
        <div className="md:sticky md:top-20 z-10 md:w-1/4 flex flex-col h-fit">
          {nbDaysArray.map((_, index) => (
            <div
              key={`circuit-day-${index}`}
              className="flex justify-between bg-brand-50 pr-4 border rounded-md"
            >
              <div
                className="w-full h-full pl-4 py-3 hover:cursor-pointer"
                onClick={() => {
                  const element = document.getElementById(`day-${index}`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <h2 className="text-base font-medium text-black w-full h-full">
                  {t('circuits.editorial.summary.day', { index: index + 1 })}
                </h2>
              </div>
              <div className="flex gap-2 w-fit">
                <StyledTooltipContainer
                  tooltipMessage={t('circuits.editorial.summary.view')}
                >
                  {
                    <EyeIcon
                      className="size-5 stroke-brand-600 hover:cursor-pointer"
                      onClick={() => {
                        onCircuitDayClick?.(index);
                      }}
                    />
                  }
                </StyledTooltipContainer>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col w-full gap-4">
          {nbDaysArray.map((displayedIndex) => (
            <Fragment key={`tarif-index-${displayedIndex}`}>
              <CircuitDayPricing
                index={displayedIndex}
                day={
                  daysTrackingMultiDayBenefits[displayedIndex] ?? { steps: [] }
                }
                totalPrice={dayPrices[displayedIndex]}
                circuitDayPrices={circuitDaysObject[displayedIndex]}
                date={addDays(selectedArrivalDate, displayedIndex)}
                currency={currency}
                language={language}
              />
              <HorizontalLine />
            </Fragment>
          ))}
          <div className="flex w-full justify-between p-4 rounded-lg bg-brand-50 border border-brand-600">
            <h3 className="text-base font-semibold">
              {t('circuits.editorial.summary.totalCircuit')}
            </h3>
            <p className="text-base font-semibold">
              {formatPrice(totalPrice)} {formatCurrency(currency)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircuitSummary;
